<template>
  <div class="w-full">
    <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" preserveAspectRatio="xMidYMid meet">
      <rect :x="padL" :y="padT" :width="plotW" :height="plotH" fill="#fafafa" rx="6" />

      <!-- y 网格与刻度 -->
      <g v-for="(t, i) in yTicks" :key="'y' + i">
        <line :x1="padL" :y1="t.y" :x2="padL + plotW" :y2="t.y" stroke="#e8e8e8" stroke-width="1" />
        <text :x="padL - 8" :y="t.y + 4" text-anchor="end" font-size="11" fill="#737373">
          {{ formatNum(t.value) }}
        </text>
      </g>

      <!-- 零基准线（温差为负时柱体向下） -->
      <line :x1="padL" :y1="zeroY" :x2="padL + plotW" :y2="zeroY" stroke="#a3a3a3" stroke-width="1" />

      <!-- 坐标轴 -->
      <line :x1="padL" :y1="padT" :x2="padL" :y2="padT + plotH" stroke="#d4d4d4" stroke-width="1" />

      <!-- 柱体 -->
      <g v-for="(b, i) in bars" :key="'b' + i">
        <rect :x="b.x" :y="b.y" :width="b.w" :height="b.h" :fill="b.color" rx="3" opacity="0.9" />
        <text
          :x="b.cx"
          :y="b.labelY"
          text-anchor="middle"
          font-size="10"
          :fill="b.color"
          font-weight="bold"
        >
          {{ formatNum(b.value) }}
        </text>
        <text :x="b.cx" :y="padT + plotH + 18" text-anchor="middle" font-size="11" fill="#737373">
          {{ b.name }}
        </text>
      </g>

      <text
        v-if="yTitle"
        :x="12"
        :y="padT + plotH / 2"
        text-anchor="middle"
        font-size="11"
        fill="#a3a3a3"
        :transform="`rotate(-90 12 ${padT + plotH / 2})`"
      >
        {{ yTitle }}
      </text>
      <text
        v-if="xTitle"
        :x="padL + plotW / 2"
        :y="H - 4"
        text-anchor="middle"
        font-size="11"
        fill="#a3a3a3"
      >
        {{ xTitle }}
      </text>
    </svg>
  </div>
</template>

<script>
export default {
  name: "BarChart",
  props: {
    // [{ name, value, color }]
    items: { type: Array, default: () => [] },
    yTitle: { type: String, default: "温差 (℃)" },
    xTitle: { type: String, default: "" },
  },
  data() {
    return { W: 800, H: 300, padL: 56, padR: 24, padT: 30, padB: 46 };
  },
  computed: {
    plotW() {
      return this.W - this.padL - this.padR;
    },
    plotH() {
      return this.H - this.padT - this.padB;
    },
    values() {
      return this.items
        .map((it) => Number(it.value))
        .filter((v) => isFinite(v));
    },
    yLo() {
      const lo = this.values.length ? Math.min(0, ...this.values) : 0;
      const hi = this.values.length ? Math.max(0, ...this.values) : 1;
      const span = hi - lo || 1;
      return lo - span * 0.15;
    },
    yHi() {
      const lo = this.values.length ? Math.min(0, ...this.values) : 0;
      const hi = this.values.length ? Math.max(0, ...this.values) : 1;
      const span = hi - lo || 1;
      return hi + span * 0.15;
    },
    zeroY() {
      return this.scaleY(0);
    },
    yTicks() {
      const span = this.yHi - this.yLo;
      if (!(span > 0)) return [];
      const candidates = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
      let step = candidates[candidates.length - 1];
      for (let i = 0; i < candidates.length; i++) {
        if (span / candidates[i] <= 5) {
          step = candidates[i];
          break;
        }
      }
      const out = [];
      const start = Math.ceil(this.yLo / step) * step;
      for (let v = start; v <= this.yHi + 1e-9; v += step) {
        out.push({ value: Math.round(v * 100) / 100, y: this.scaleY(v) });
      }
      return out;
    },
    bars() {
      const n = this.items.length;
      if (!n) return [];
      const slot = this.plotW / n;
      const w = Math.max(12, Math.min(64, slot * 0.55));
      return this.items.map((it, i) => {
        const cx = this.padL + slot * i + slot / 2;
        const v = Number(it.value);
        const ok = isFinite(v);
        const yv = ok ? this.scaleY(v) : this.zeroY;
        const h = Math.max(ok ? 1.5 : 0, Math.abs(this.zeroY - yv));
        const y = v >= 0 ? yv : this.zeroY;
        return {
          name: it.name,
          value: ok ? v : 0,
          color: it.color || "#8b5cf6",
          x: cx - w / 2,
          w,
          cx,
          y,
          h,
          labelY: v >= 0 ? y - 6 : y + h + 13,
        };
      });
    },
  },
  methods: {
    scaleY(y) {
      const span = this.yHi - this.yLo;
      if (!span) return this.padT + this.plotH / 2;
      return this.padT + (1 - (y - this.yLo) / span) * this.plotH;
    },
    formatNum(v) {
      const n = Number(v);
      if (isNaN(n)) return "-";
      return String(Math.round(n * 100) / 100);
    },
  },
};
</script>
